/* ==========================================
   SnS Collection Randomizer (SNSCR)
   - Modes: off | on_load | by_days
   - Fixed products first on initial render
   - Optional per-browser remembered order (1-30 days, default 7)
   - Infinite scroll: randomizes ONLY newly added products
   - Existing visible products NEVER move after infinite scroll
   - No flash on initial/Dawn grid render
   ========================================== */

(function () {
  "use strict";

  // Keeps the same on_load seed during the lifetime of this page.
  const runtimeLoadSeeds = new Map();

  // ---------- Helpers ----------
  function isLikelyBot() {
    const ua = (navigator.userAgent || "").toLowerCase();
    return /bot|crawl|spider|slurp|bingpreview|duckduckbot|yandex|baidu|facebookexternalhit|twitterbot|pinterest|linkedinbot|embedly|quora|whatsapp|telegrambot/.test(
      ua
    );
  }

  function mulberry32(seed) {
    return function () {
      let t = (seed += 0x6d2b79f5);
      t = Math.imul(t ^ (t >>> 15), t | 1);
      t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
      return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
  }

  function shuffleInPlace(arr, rng) {
    for (let i = arr.length - 1; i > 0; i--) {
      const j = Math.floor(rng() * (i + 1));
      [arr[i], arr[j]] = [arr[j], arr[i]];
    }
    return arr;
  }

  function stableSeedFromString(str) {
    let h = 2166136261;
    for (let i = 0; i < str.length; i++) {
      h ^= str.charCodeAt(i);
      h = Math.imul(h, 16777619);
    }
    return (h >>> 0) || 1;
  }

  function getDayBucket(days) {
    const ms = 24 * 60 * 60 * 1000;
    return Math.floor(Date.now() / (days * ms));
  }

  function createRandomSeedValue() {
    let value;

    try {
      if (window.crypto && window.crypto.getRandomValues) {
        const values = new Uint32Array(2);
        window.crypto.getRandomValues(values);
        value = String(values[0]) + "-" + String(values[1]);
      }
    } catch (e) {}

    if (!value) {
      value =
        String(Date.now()) +
        "-" +
        String(Math.floor(Math.random() * 1000000000));
    }

    return value;
  }

  function getRuntimeLoadSeed(collectionHandle) {
    if (runtimeLoadSeeds.has(collectionHandle)) {
      return runtimeLoadSeeds.get(collectionHandle);
    }

    const value = createRandomSeedValue();
    runtimeLoadSeeds.set(collectionHandle, value);
    return value;
  }

  function getRememberDays(cfgEl) {
    const raw = cfgEl.getAttribute("data-remember-days") || "7";
    return Math.max(1, Math.min(30, parseInt(raw, 10) || 7));
  }

  function shouldRememberVisitor(cfgEl) {
    return cfgEl.getAttribute("data-remember-visitor") === "true";
  }

  function getVisitorStorageKey(collectionHandle) {
    return "snscr_visitor_seed_v1:" + collectionHandle;
  }

  function clearVisitorSeed(collectionHandle) {
    try {
      localStorage.removeItem(getVisitorStorageKey(collectionHandle));
    } catch (e) {}
  }

  function getVisitorSeed(collectionHandle, days) {
    const key = getVisitorStorageKey(collectionHandle);
    const now = Date.now();
    const ttl = days * 24 * 60 * 60 * 1000;

    try {
      const raw = localStorage.getItem(key);

      if (raw) {
        const stored = JSON.parse(raw);

        if (
          stored &&
          typeof stored.seed === "string" &&
          stored.seed &&
          typeof stored.expiresAt === "number" &&
          stored.expiresAt > now &&
          stored.days === days
        ) {
          return stored.seed;
        }

        localStorage.removeItem(key);
      }

      const seed = createRandomSeedValue();

      localStorage.setItem(
        key,
        JSON.stringify({
          seed: seed,
          createdAt: now,
          expiresAt: now + ttl,
          days: days
        })
      );

      return seed;
    } catch (e) {
      // Privacy/storage restrictions: gracefully fall back to this page load.
      return getRuntimeLoadSeed(collectionHandle);
    }
  }

  function getGridAndCfg() {
    const grid = document.getElementById("product-grid");
    if (!grid) return null;

    const cfgEl = grid.closest("[data-snscr-config]") || grid;

    if (!cfgEl || !cfgEl.hasAttribute("data-snscr-config")) {
      return null;
    }

    return { grid, cfgEl };
  }

  function getSeedBase(cfgEl, collectionHandle) {
    const mode = (cfgEl.getAttribute("data-mode") || "off").trim();

    const daysStr = cfgEl.getAttribute("data-days") || "7";
    const days = Math.max(
      1,
      Math.min(180, parseInt(daysStr, 10) || 7)
    );

    let seedBase = "snscr:" + collectionHandle + ":";

    /*
     * by_days keeps its original GLOBAL behavior:
     * all visitors share the same order for that time bucket.
     *
     * on_load can now optionally remember an INDIVIDUAL browser seed
     * in localStorage for 1-30 days.
     */
    if (mode === "by_days") {
      seedBase += "bucket:" + String(getDayBucket(days));
    } else if (shouldRememberVisitor(cfgEl)) {
      const rememberDays = getRememberDays(cfgEl);
      seedBase +=
        "visitor:" +
        getVisitorSeed(collectionHandle, rememberDays);
    } else {
      clearVisitorSeed(collectionHandle);
      seedBase += "load:" + getRuntimeLoadSeed(collectionHandle);
    }

    return seedBase;
  }

  // =========================================================
  // FULL RANDOMIZATION
  // Used ONLY for initial load / complete Dawn grid replacement
  // =========================================================

  function runOnce() {
    const ctx = getGridAndCfg();
    if (!ctx) return;

    const { grid, cfgEl } = ctx;

    if (isLikelyBot()) {
      grid.setAttribute("data-snscr-ready", "true");
      return;
    }

    const enabled =
      cfgEl.getAttribute("data-enabled") === "true";

    const mode =
      (cfgEl.getAttribute("data-mode") || "off").trim();

    if (!enabled || mode === "off") {
      grid.setAttribute("data-snscr-ready", "true");
      return;
    }

    const collectionHandle =
      (cfgEl.getAttribute("data-collection-handle") || "").trim();

    if (!collectionHandle) {
      grid.setAttribute("data-snscr-ready", "true");
      return;
    }

    grid.setAttribute("data-snscr-ready", "false");

    try {
      const respectFixed =
        cfgEl.getAttribute("data-respect-fixed") === "true";

      const fixedRaw =
        (cfgEl.getAttribute("data-fixed") || "").trim();

      const fixedList = fixedRaw
        ? fixedRaw
            .split("|")
            .map((s) => s.trim())
            .filter(Boolean)
        : [];

      const items = Array.from(grid.children).filter(
        (el) => el && el.nodeType === 1
      );

      if (items.length < 2) {
        grid.setAttribute("data-snscr-ready", "true");
        return;
      }

      const byHandle = new Map();

      for (const el of items) {
        const h = el.getAttribute("data-product-handle");
        if (h) {
          byHandle.set(h, el);
        }
      }

      const fixedEls = [];
      const fixedUsed = new Set();

      if (
        respectFixed &&
        fixedList.length &&
        byHandle.size
      ) {
        for (const h of fixedList) {
          const el = byHandle.get(h);

          if (el && !fixedUsed.has(el)) {
            fixedEls.push(el);
            fixedUsed.add(el);
          }
        }
      }

      const rest = items.filter(
        (el) => !fixedUsed.has(el)
      );

      const seedBase =
        getSeedBase(cfgEl, collectionHandle) + ":initial";

      const seed = stableSeedFromString(seedBase);
      const rng = mulberry32(seed);

      shuffleInPlace(rest, rng);

      const finalOrder = fixedEls.concat(rest);

      const frag =
        document.createDocumentFragment();

      for (const el of finalOrder) {
        frag.appendChild(el);
      }

      grid.appendChild(frag);

      // Mark current products as already processed so a duplicated
      // infinite-scroll event can never reinterpret old nodes as new.
      for (const el of finalOrder) {
        el.setAttribute("data-snscr-batch-processed", "true");
      }

      grid.setAttribute("data-snscr-ready", "true");
    } catch (e) {
      grid.setAttribute("data-snscr-ready", "true");
    }
  }

  // =========================================================
  // INCREMENTAL RANDOMIZATION
  // Used ONLY after infinite scroll
  // Existing products are NEVER moved
  // =========================================================

  function runIncremental(productsAdded) {
    const ctx = getGridAndCfg();
    if (!ctx) return;

    const { grid, cfgEl } = ctx;

    if (isLikelyBot()) return;

    const enabled =
      cfgEl.getAttribute("data-enabled") === "true";

    const mode =
      (cfgEl.getAttribute("data-mode") || "off").trim();

    if (!enabled || mode === "off") return;

    const collectionHandle =
      (cfgEl.getAttribute("data-collection-handle") || "").trim();

    if (!collectionHandle) return;

    const count = Math.max(
      0,
      parseInt(productsAdded, 10) || 0
    );

    if (!count) return;

    try {
      const allItems = Array.from(grid.children).filter(
        (el) => el && el.nodeType === 1
      );

      /*
       * InfiniteScroll appends the new products at the end.
       * Therefore ONLY the last N nodes belong to this batch.
       */
      const candidateBatch = allItems.slice(
        Math.max(0, allItems.length - count)
      );

      /*
       * Protect against an accidental duplicate event.
       */
      const newBatch = candidateBatch.filter(
        (el) =>
          el.getAttribute("data-snscr-batch-processed") !== "true"
      );

      if (!newBatch.length) return;

      if (newBatch.length === 1) {
        newBatch[0].setAttribute(
          "data-snscr-batch-processed",
          "true"
        );
        return;
      }

      /*
       * IMPORTANT:
       * Fixed products are NOT re-applied here.
       *
       * Moving a newly loaded fixed product to the beginning
       * would move products the visitor has already seen,
       * which is exactly what this version prevents.
       */

      const signature = newBatch
        .map((el, index) => {
          return (
            el.getAttribute("data-product-handle") ||
            el.getAttribute("data-product-id") ||
            String(index)
          );
        })
        .join("|");

      const seedBase =
        getSeedBase(cfgEl, collectionHandle) +
        ":append:" +
        signature;

      const seed =
        stableSeedFromString(seedBase);

      const rng =
        mulberry32(seed);

      shuffleInPlace(newBatch, rng);

      const frag =
        document.createDocumentFragment();

      for (const el of newBatch) {
        el.setAttribute(
          "data-snscr-batch-processed",
          "true"
        );

        frag.appendChild(el);
      }

      /*
       * appendChild MOVES only these new nodes.
       * Every previously displayed product stays exactly
       * where it was.
       */
      grid.appendChild(frag);

    } catch (e) {
      // Existing grid remains untouched.
    }
  }

  // Infinite scroll: randomize ONLY the products just added.
  window.addEventListener(
    "infinitescroll:loaded",
    function (event) {
      const productsAdded =
        event &&
        event.detail &&
        event.detail.productsAdded
          ? event.detail.productsAdded
          : 0;

      runIncremental(productsAdded);
    }
  );

  // Initial load
  document.addEventListener(
    "DOMContentLoaded",
    function () {
      runOnce();

      /*
       * Dawn filters/sorting can replace the ENTIRE grid.
       * A genuinely new grid arrives with
       * data-snscr-ready="false", so in that case
       * a complete randomization is appropriate.
       */
      const container =
        document.getElementById(
          "ProductGridContainer"
        ) || document.body;

      const mo =
        new MutationObserver(function () {
          const grid =
            document.getElementById(
              "product-grid"
            );

          if (
            grid &&
            grid.getAttribute(
              "data-snscr-ready"
            ) === "false"
          ) {
            runOnce();
          }
        });

      mo.observe(container, {
        childList: true,
        subtree: true
      });
    }
  );
})();
